import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Button from '../../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { humanizeSupabaseError } from '../../../../lib/supabaseErrors';
import { openPrintUrl } from '../../../../lib/openPrint';
import {
  CHART_COLORS,
  bucketByDay,
  bucketByMonth,
  buildPrintReportUrl,
  downloadCsv,
  formatDateRangeLabel,
  studentNameLookup,
} from '../../reportUtils';
import { KpiCard, ChartCard, EmptyState } from './IepProgressReport';

// ---------------------------------------------------------------------------
// Data Collection Report
//
// The question this answers: are we actually collecting data on our goals?
// It's easy for a busy team to enter a goal and then not touch it again
// for weeks. This report surfaces those "stale" goals and shows the
// team's data-collection cadence.
//
// Unlike the other reports, this one has to *fetch* goal_progress_entries
// on demand because the IEPGoalsContext only loads entries for a single
// goal at a time (via listProgressEntries). We use the new
// listAllProgressEntries helper the context now exposes.
// ---------------------------------------------------------------------------

const STALE_DAYS = 14;

const DataCollectionReport = ({ dateRange, studentFilter, printMode = false }) => {
  const { students } = useStudents();
  const { goals, listAllProgressEntries } = useIEPGoals();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const animate = !printMode;
  const handlePrint = () => {
    const url = buildPrintReportUrl('data-collection', { dateRange, studentFilter });
    openPrintUrl(url);
  };

  // Reload the entries whenever the date range changes. The context
  // handles both live-Supabase and demo-mode localStorage inside
  // listAllProgressEntries, so we don't need to branch here.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAllProgressEntries({ from: dateRange?.from, to: dateRange?.to })
      .then((rows) => {
        if (!cancelled) {
          setEntries(rows || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            humanizeSupabaseError(
              err,
              'Could not load goal progress entries.',
            ),
          );
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listAllProgressEntries, dateRange?.from, dateRange?.to]);

  const rollups = useMemo(() => {
    const nameFor = studentNameLookup(students);
    const activeStudents = (Array.isArray(students) ? students : []).filter(
      (s) => (s.status || 'active') !== 'archived',
    );
    const filterActive = studentFilter && studentFilter.size > 0;
    const inScopeStudents = filterActive
      ? activeStudents.filter((s) => studentFilter.has(s.id))
      : activeStudents;
    const inScopeStudentIds = new Set(inScopeStudents.map((s) => s.id));

    // Goals in scope: active students, and only ones still on the roadmap
    // (in_progress or on_hold). Mastered / discontinued goals don't need
    // fresh data.
    const scopedGoals = (Array.isArray(goals) ? goals : []).filter((g) => {
      if (!inScopeStudentIds.has(g.studentId)) return false;
      const status = g.status || 'in_progress';
      return status === 'in_progress' || status === 'on_hold';
    });
    const scopedGoalIds = new Set(scopedGoals.map((g) => g.id));

    const scopedEntries = entries.filter((e) => scopedGoalIds.has(e.goalId));

    // KPIs
    const totalEntries = scopedEntries.length;
    const goalsWithData = new Set(scopedEntries.map((e) => e.goalId)).size;
    const emptyGoals = Math.max(scopedGoals.length - goalsWithData, 0);

    // Cadence chart: daily if <=60 days, else monthly.
    const cadenceRows = scopedEntries.map((e) => ({ observedOn: e.observedOn }));
    let cadenceData;
    let cadenceGranularity;
    if (dateRange?.from && dateRange?.to) {
      const days =
        (new Date(dateRange.to) - new Date(dateRange.from)) /
        (1000 * 60 * 60 * 24);
      if (days <= 60) {
        cadenceData = bucketByDay(cadenceRows, 'observedOn', dateRange);
        cadenceGranularity = 'day';
      } else {
        cadenceData = bucketByMonth(
          cadenceRows,
          'observedOn',
          null,
          dateRange,
        );
        cadenceGranularity = 'month';
      }
    } else {
      cadenceData = bucketByMonth(cadenceRows, 'observedOn', null, dateRange);
      cadenceGranularity = 'month';
    }

    // Per-goal rollup, keyed on the goal itself so we can flag stale goals
    // even if there are zero entries in the current window.
    const entriesByGoal = new Map();
    for (const e of scopedEntries) {
      const arr = entriesByGoal.get(e.goalId) || [];
      arr.push(e);
      entriesByGoal.set(e.goalId, arr);
    }

    const todayIso = new Date().toISOString().slice(0, 10);
    const perGoal = scopedGoals.map((g) => {
      const gEntries = entriesByGoal.get(g.id) || [];
      const sorted = [...gEntries].sort((a, b) =>
        String(b.observedOn || '').localeCompare(String(a.observedOn || '')),
      );
      const latest = sorted[0];
      const latestDate = latest?.observedOn || null;
      const daysSince = latestDate
        ? Math.round(
            (new Date(todayIso) - new Date(latestDate)) /
              (1000 * 60 * 60 * 24),
          )
        : null;
      return {
        id: g.id,
        studentName: nameFor.get(g.studentId) || 'Unknown',
        goalArea: g.goalArea || 'Unspecified',
        specific: g.specific || '',
        entryCount: gEntries.length,
        latestDate,
        daysSince,
        progressPercent: Number(g.progressPercent ?? 0),
        stale:
          latestDate === null || (daysSince !== null && daysSince > STALE_DAYS),
      };
    });

    // Order: stale first (worst offenders), then by lowest entry count
    perGoal.sort((a, b) => {
      if (a.stale !== b.stale) return a.stale ? -1 : 1;
      return a.entryCount - b.entryCount;
    });

    const staleCount = perGoal.filter((r) => r.stale).length;

    return {
      totalEntries,
      totalGoals: scopedGoals.length,
      goalsWithData,
      emptyGoals,
      staleCount,
      cadenceData,
      cadenceGranularity,
      perGoal,
    };
  }, [students, goals, entries, studentFilter, dateRange]);

  const handleExportCsv = () => {
    const filename = `data-collection-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(
      filename,
      rollups.perGoal.map((r) => ({
        student: r.studentName,
        goalArea: r.goalArea,
        specific: r.specific,
        entries: r.entryCount,
        latestDate: r.latestDate || '',
        daysSince: r.daysSince ?? '',
        progressPercent: r.progressPercent,
        stale: r.stale ? 'yes' : 'no',
      })),
      [
        { key: 'student', label: 'Student' },
        { key: 'goalArea', label: 'Goal area' },
        { key: 'specific', label: 'Goal' },
        { key: 'entries', label: 'Data points (in range)' },
        { key: 'latestDate', label: 'Latest entry' },
        { key: 'daysSince', label: 'Days since last entry' },
        { key: 'progressPercent', label: 'Progress %' },
        { key: 'stale', label: `Stale (>${STALE_DAYS} days)` },
      ],
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="Activity"
          label="Data points"
          value={String(rollups.totalEntries)}
          tone="primary"
          subtitle={formatDateRangeLabel(dateRange)}
        />
        <KpiCard
          icon="Target"
          label="Active goals"
          value={String(rollups.totalGoals)}
          tone="primary"
          subtitle={`${rollups.goalsWithData} with data in range`}
        />
        <KpiCard
          icon="AlertCircle"
          label={`Stale (>${STALE_DAYS}d)`}
          value={String(rollups.staleCount)}
          tone={rollups.staleCount > 0 ? 'danger' : 'success'}
          subtitle="No data or last entry too old"
        />
        <KpiCard
          icon="Inbox"
          label="Never logged"
          value={String(rollups.emptyGoals)}
          tone={rollups.emptyGoals > 0 ? 'warning' : 'success'}
          subtitle="Goals with zero data points in range"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Data collection cadence
          </h2>
          <p className="text-sm text-muted-foreground">
            A goal is <strong>stale</strong> when it hasn't received a data
            point in {STALE_DAYS} days. This report is a hit list of the goals
            that need attention this week.
          </p>
        </div>
        {!printMode && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handlePrint}
              iconName="Printer"
              iconPosition="left"
              title={
                rollups.perGoal.length === 0
                  ? 'Opens the print view — will show "no goals with data in this window".'
                  : 'Open the print-ready view for this report'
              }
            >
              Print / Save as PDF
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              iconName="Download"
              iconPosition="left"
              disabled={rollups.perGoal.length === 0}
            >
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-foreground">
          <p className="font-medium">Couldn't load progress entries</p>
          <p className="text-muted-foreground mt-1">{error}</p>
        </div>
      )}

      {loading && (
        <div className="p-4 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground text-center">
          Loading progress entries…
        </div>
      )}

      {!loading && rollups.perGoal.length === 0 ? (
        <EmptyState
          icon="Target"
          title="No active goals for this scope"
          body="Add an IEP goal to a student, or widen the student filter, and this report will show how consistently you're logging data on it."
        />
      ) : (
        !loading && (
          <>
            <ChartCard
              title={
                rollups.cadenceGranularity === 'day'
                  ? 'Data points per day'
                  : 'Data points per month'
              }
            >
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={rollups.cadenceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="label"
                    stroke="#6B7280"
                    fontSize={12}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    stroke="#6B7280"
                    fontSize={12}
                    allowDecimals={false}
                  />
                  {!printMode && (
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                      }}
                    />
                  )}
                  <Bar
                    dataKey="value"
                    fill={CHART_COLORS.primary}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={animate}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <div className="bg-surface border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="font-medium text-foreground">
                  Per-goal cadence
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sorted so stale goals show up first.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="p-3">Student</th>
                      <th className="p-3">Goal area</th>
                      <th className="p-3">Data points</th>
                      <th className="p-3">Latest entry</th>
                      <th className="p-3">Progress</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rollups.perGoal.map((r) => (
                      <tr key={r.id} className="hover:bg-muted/30">
                        <td className="p-3 font-medium text-foreground">
                          {r.studentName}
                        </td>
                        <td className="p-3 text-foreground capitalize">
                          {r.goalArea}
                        </td>
                        <td className="p-3 text-foreground">{r.entryCount}</td>
                        <td className="p-3 text-foreground">
                          {r.latestDate
                            ? `${new Date(
                                `${r.latestDate}T12:00:00`,
                              ).toLocaleDateString()} · ${r.daysSince}d ago`
                            : 'Never'}
                        </td>
                        <td className="p-3 text-foreground">
                          {Number.isFinite(r.progressPercent)
                            ? `${r.progressPercent}%`
                            : '—'}
                        </td>
                        <td className="p-3">
                          {r.stale ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                              Stale
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                              On cadence
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default DataCollectionReport;
